/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { Autocomplete, Grid, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectLocalCurrency,
  selectCurrencyList,
  selectVisualFilters,
} from 'modules/dashboard/selectors';
import { dashboardActions } from 'modules/dashboard/slice';
import { useEffect, useState } from 'react';
import { getCurrencySchema } from 'modules/dashboard/components/tab-container/inner-filter/functions';
import { CURRENCY_TYPES } from 'modules/dashboard/constants';

/**
 * Navigation bar currency dropdown common component that uses
 * across modules for display currency dropdown and select currency
 */
const NavbarCurrencyDropdown = () => {
  const dispatch = useDispatch();

  const localCurrency = useSelector(selectLocalCurrency);
  const currencyList = useSelector(selectCurrencyList);
  const visualFilters = useSelector(selectVisualFilters);
  //
  const [isCurrencyDropdownDisabled, setIsCurrencyDropdownDisabled] = useState(false);
  // set currency filter based on selected currency value in dropdown
  useEffect(() => {
    dispatch(
      dashboardActions.setCurrencyFilter(
        getCurrencySchema(
          visualFilters,
          localCurrency !== CURRENCY_TYPES.USD ? CURRENCY_TYPES.RC : CURRENCY_TYPES.USD
        )
      )
    );
  }, [localCurrency]);
  // disable currency dropdown when only USD is there
  useEffect(() => {
    if (currencyList?.length === 1) {
      setIsCurrencyDropdownDisabled(true);
    } else {
      setIsCurrencyDropdownDisabled(false);
    }
  }, [currencyList]);
    // set USD when change hotel when local currency other than USD selected and selected hotel does not have selected local currency
    useEffect(() => {
      if(currencyList?.every(curr => curr.id !== localCurrency)){
        dispatch(dashboardActions.setLocalCurrency(CURRENCY_TYPES.USD));
      }
    }, [currencyList]);
  //
  return (
    <Grid container flexDirection="row" justifyContent="flex-end" alignItems="center" wrap="nowrap">
      <div  className='currency-dropdown'>
      <Autocomplete
        options={currencyList ?? []}
        getOptionLabel={(option) => (option.label ? option.label : '')}
        isOptionEqualToValue={(option, value) => option && value && option.id === value.id}
        filterSelectedOptions
        disableClearable
        value={{
          id: localCurrency,
          label: localCurrency,
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label=""
            size="small"
            sx={{
              '.MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: 'gray',
                color: 'gray',
              },
              '& .MuiAutocomplete-endAdornment' :{
                top:  'calc(50% - 0px)'
              }
            }}
          />
        )}
        onChange={(event, value) => dispatch(dashboardActions.setLocalCurrency(value.id))}
        fullWidth
        sx={{
          minWidth: 100,
          '& .MuiOutlinedInput-root': {
            color: '#e5e5e5',
          },
          '& .MuiSvgIcon-root': {
            color: '#e5e5e5',
            width: '2rem',
            height: '2rem',
            top: 0,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '& .MuiAutocomplete-input': {
            fontSize: { md: 13, lg: 15 },
            fontWeight: 'bold',
          },
        }}
        disabled={isCurrencyDropdownDisabled}
      />
      </div>
    </Grid>
  );
};
//
export default NavbarCurrencyDropdown;
